"use client";

import { useEffect, useRef, useState } from "react";

interface CardExpandPopupProps {
  /** Prompt text displayed at the top of the popup. */
  prompt: string;
  /** When the prompt was sent. Optional small timestamp above the prompt-box. */
  promptTime?: Date | null;
  /** The expanded card content — a ResponseCard or JudgeCard rendered with
   *  `inPopup` so its body grows to fill its parent and owns its own scroll. */
  children: React.ReactNode;
  /** Backdrop click + ESC fire this. */
  onClose: () => void;
}

/**
 * Desktop-web-only modal that expands a single output card to ~75% of the
 * viewport width with the query above it for easier reading.
 *
 * SCROLL CHOREOGRAPHY (coordinated wheel handler, no position:sticky):
 *
 *   Initial state — prompt at top, card BELOW it. The card's height is
 *   locked to the popup's full content height, so the bottom portion of
 *   the card is clipped by the panel's overflow:hidden initially.
 *
 *   Wheel DOWN:
 *     • If prompt is still visible: translateY a wrapper containing
 *       prompt + card upward, sliding the prompt off the top.
 *     • Once the prompt is fully hidden (offset reaches promptHeight),
 *       additional wheel deltas pass through to the card body's native
 *       scroll. The card occupies the entire popup at this point.
 *
 *   Wheel UP:
 *     • If the card body has scrollTop > 0: card body scrolls up first.
 *     • Once card body hits scrollTop:0 AND the wheel still has more
 *       upward delta, the wrapper translates back down — prompt slides
 *       back into view.
 *
 * Why a wheel handler (not sticky / not IntersectionObserver):
 *   Tried both. Safari + the popup's compositing context broke
 *   position:sticky reliably; CSS-class-driven absolute positioning lost
 *   the specificity war against inline `position:relative` on the card
 *   outer. A direct wheel handler bypasses all CSS positioning quirks
 *   and gives us exactly the coordinated scroll the user described.
 */
export default function CardExpandPopup({
  prompt,
  promptTime,
  children,
  onClose,
}: CardExpandPopupProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  // Ref + state pair: the ref gives the wheel handler an instant read of
  // current state without needing to be re-bound. The state drives the
  // render (the translateY transform).
  const promptOffsetRef = useRef(0);
  const [promptOffset, setPromptOffsetState] = useState(0);
  const promptHeightRef = useRef(0);

  const updateOffset = (value: number) => {
    const clamped = Math.max(0, Math.min(promptHeightRef.current, value));
    promptOffsetRef.current = clamped;
    setPromptOffsetState(clamped);
  };

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Measure prompt block height (+ marginBottom) so the wheel handler
  // knows how far to allow the wrapper to slide.
  useEffect(() => {
    if (!promptRef.current) return;
    const measure = () => {
      if (!promptRef.current) return;
      promptHeightRef.current = promptRef.current.getBoundingClientRect().height + 10;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(promptRef.current);
    return () => ro.disconnect();
  }, [prompt]);

  // Coordinated wheel handler — split scroll between prompt-slide + card-body-scroll.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const handleWheel = (e: WheelEvent) => {
      const deltaY = e.deltaY;
      if (deltaY === 0) return;

      const promptHeight = promptHeightRef.current;
      const currentOffset = promptOffsetRef.current;

      const cardBody = cardWrapperRef.current?.querySelector<HTMLElement>(
        ".judge-card-body, .response-card-body"
      );

      if (deltaY > 0) {
        // Scrolling DOWN
        if (currentOffset < promptHeight) {
          // Prompt still partially visible — slide it up.
          e.preventDefault();
          const want = currentOffset + deltaY;
          if (want > promptHeight && cardBody) {
            // Spillover past prompt height — finish hiding the prompt
            // and apply the rest to the card body's scroll.
            updateOffset(promptHeight);
            cardBody.scrollTop += want - promptHeight;
          } else {
            updateOffset(want);
          }
        }
        // Else: prompt fully hidden — native event handles card body scroll.
      } else {
        // Scrolling UP
        if (cardBody && cardBody.scrollTop > 0) {
          // Card body has scroll to consume.
          const after = cardBody.scrollTop + deltaY; // deltaY < 0
          if (after < 0 && currentOffset > 0) {
            // Spillover above body's top — clamp body to 0, slide prompt back.
            e.preventDefault();
            cardBody.scrollTop = 0;
            updateOffset(currentOffset + after); // after is negative
          }
          // Else: body has room above scrollTop=0 — native scroll handles it.
        } else if (currentOffset > 0) {
          // Card body at top, prompt is partially hidden — slide it back.
          e.preventDefault();
          updateOffset(currentOffset + deltaY);
        }
      }
    };

    // passive: false so preventDefault works.
    panel.addEventListener("wheel", handleWheel, { passive: false });
    return () => panel.removeEventListener("wheel", handleWheel);
  }, []);

  return (
    <div
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.96)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: "24px",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "75vw",
          height: "90vh",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          padding: "10px 24px 20px 24px",
          position: "relative",
          // overflow:hidden so the prompt + card extending past the panel
          // get clipped cleanly. The card height is set to the full panel
          // content height so it fills the popup once prompt is hidden.
          overflow: "hidden",
        }}
      >
        {/* Close X — outside the translating wrapper so it stays put. */}
        <button
          className="btn-ghost"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 8,
            right: 10,
            padding: 4,
            lineHeight: 0,
            zIndex: 10,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>

        {/* Translating wrapper: prompt above + card below, both slide up
            together as the user wheels down. Once promptOffset reaches
            promptHeight the prompt is fully off-screen and the card
            occupies the whole popup viewport. */}
        <div
          style={{
            transform: `translateY(-${promptOffset}px)`,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Prompt block */}
          <div
            ref={promptRef}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              marginBottom: 10,
              flexShrink: 0,
            }}
          >
            {promptTime && (
              <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "0 36px 0 4px" }}>
                {promptTime.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {promptTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            )}
            <div
              style={{
                padding: "10px 14px",
                background: "var(--surface-hover)",
                borderRadius: "var(--radius-lg)",
                color: "var(--text)",
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {prompt}
            </div>
          </div>

          {/* Card area — LOCKED at the full panel-content height so that
              once the prompt is hidden, the card exactly fills the
              visible popup. flexShrink:0 keeps the height fixed. */}
          <div
            ref={cardWrapperRef}
            className="card-expand-popup-body"
            style={{
              height: "calc(90vh - 30px)",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
